import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { queryErrorEvents } from '@/features/superuser/data/errorEvents';
import { useNotifications } from '@/providers/notificationHooks';
import { queryKeys } from '@/shared/hooks';
import { useAccessToken } from '@/shared/hooks/auth';
import { Inline, Surface, Text } from '@/ui';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { formatDate } from '@/ui/formatters/formatDate';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColDef } from '@/ui/primitives';
import type { ErrorEventDto, QueryResult } from '@flowqa/shared';

export const ErrorEventsTable = (): ReactElement => {
  const accessToken = useAccessToken();
  const { notify } = useNotifications();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [searchValue]);

  const jsonReplacer = useCallback((_key: string, value: unknown): unknown => {
    if (value instanceof Date) return value.toISOString();
    return value;
  }, []);

  const copyErrorEventToClipboard = useCallback(
    (row: ErrorEventDto): void => {
      const payload = JSON.stringify(row, jsonReplacer, 2);
      void navigator.clipboard.writeText(payload);
      notify({ message: 'Error data copied to clipboard', tone: 'green' });
    },
    [jsonReplacer, notify],
  );

  const formatRequestBodyPreview = useCallback((value: ErrorEventDto['requestBody']): string => {
    if (value === null) return '-';
    try {
      const raw = JSON.stringify(value);
      if (raw.length === 0) return '-';
      return raw.length > 180 ? `${raw.slice(0, 180)}…` : raw;
    } catch {
      return '[unserializable]';
    }
  }, []);

  const columns = useMemo<Array<GridColDef<ErrorEventDto>>>(() => {
    const createdAtColumn: GridColDef<ErrorEventDto> = {
      field: 'createdAt',
      headerName: 'Created',
      type: 'dateTime',
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row): Date | null => {
        const raw = row.createdAt;
        if (raw instanceof Date) return raw;
        if (typeof raw === 'string') {
          const parsed = new Date(raw);
          return Number.isNaN(parsed.valueOf()) ? null : parsed;
        }
        return null;
      },
      renderCell: (params) => (
        <Text role="body" component="span">
          {formatDate(params.row.createdAt, { includeTime: true }) || '-'}
        </Text>
      ),
    };
    const statusColumn: GridColDef<ErrorEventDto> = {
      field: 'statusCode',
      headerName: 'Status',
      type: 'number',
      width: 110,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.statusCode}
        </Text>
      ),
    };
    const codeColumn: GridColDef<ErrorEventDto> = {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.code}
        </Text>
      ),
    };
    const messageColumn: GridColDef<ErrorEventDto> = {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      minWidth: 240,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.message}
        </Text>
      ),
    };
    const routeColumn: GridColDef<ErrorEventDto> = {
      field: 'route',
      headerName: 'Route',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.route ?? params.row.url}
        </Text>
      ),
    };
    const methodColumn: GridColDef<ErrorEventDto> = {
      field: 'method',
      headerName: 'Method',
      width: 120,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.method}
        </Text>
      ),
    };
    const requestBodyColumn: GridColDef<ErrorEventDto> = {
      field: 'requestBody',
      headerName: 'Request',
      flex: 2,
      minWidth: 240,
      renderCell: (params) => (
        <Text role="body" component="span">
          {formatRequestBodyPreview(params.row.requestBody)}
        </Text>
      ),
    };
    const errorIdColumn: GridColDef<ErrorEventDto> = {
      field: 'errorId',
      headerName: 'Error ID',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.errorId}
        </Text>
      ),
    };
    const correlationIdColumn: GridColDef<ErrorEventDto> = {
      field: 'correlationId',
      headerName: 'Correlation ID',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.correlationId}
        </Text>
      ),
    };
    const orgIdColumn: GridColDef<ErrorEventDto> = {
      field: 'orgId',
      headerName: 'Org',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.orgId ?? '-'}
        </Text>
      ),
    };
    const userIdColumn: GridColDef<ErrorEventDto> = {
      field: 'userId',
      headerName: 'User',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.userId ?? '-'}
        </Text>
      ),
    };
    const projectIdColumn: GridColDef<ErrorEventDto> = {
      field: 'projectId',
      headerName: 'Project',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Text role="body" component="span">
          {params.row.projectId ?? '-'}
        </Text>
      ),
    };
    const actionColumn: GridColDef<ErrorEventDto> = {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="sm"
          fullWidthOnMobile={false}
          onClick={() => {
            copyErrorEventToClipboard(params.row);
          }}
        >
          Copy
        </Button>
      ),
    };
    return [
      createdAtColumn,
      statusColumn,
      codeColumn,
      messageColumn,
      routeColumn,
      methodColumn,
      requestBodyColumn,
      errorIdColumn,
      correlationIdColumn,
      orgIdColumn,
      userIdColumn,
      projectIdColumn,
      actionColumn,
    ];
  }, [copyErrorEventToClipboard, formatRequestBodyPreview]);

  const queryFn = async (input: GridQueryInput): Promise<QueryResult<ErrorEventDto>> => {
    return await queryErrorEvents(input, accessToken);
  };

  return (
    <>
      <Inline justify="space-between" align="center" wrap="wrap" gap="sm">
        <Text role="sectionTitle">Error Events</Text>
        <Inline gap="sm" align="center" wrap="wrap">
          <TextField
            label="Search"
            placeholder="Search errorId, message, route, org..."
            value={searchValue}
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
            fullWidth={false}
          />
          <Button
            variant="outlined"
            size="sm"
            disabled={searchValue.trim().length === 0}
            onClick={() => {
              setSearchValue('');
              setDebouncedSearchValue('');
            }}
          >
            Clear
          </Button>
        </Inline>
      </Inline>
      <Surface padding="none" border={false} sx={{ width: '100%', minWidth: 0 }}>
        <ServerDataTable<ErrorEventDto>
          queryPath="superuser-error-events"
          queryKeyPrefix={queryKeys.superuserErrorEvents()}
          queryFn={queryFn}
          columns={columns}
          getRowId={(row) => row.id}
          token={accessToken}
          pageSizeOptions={[10, 25, 50]}
          initialPageSize={10}
          autoHeight
          noRowsText="No error events"
          disableColumnMenu
          search={debouncedSearchValue || undefined}
          initialSortModel={[{ field: 'createdAt', sort: 'desc' }]}
        />
      </Surface>
    </>
  );
};
